import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'react-toastify';
import apiService, { api } from '../services/api';
import { formatCurrency } from '../utils/formatters';
import { FaSync } from 'react-icons/fa';

const ProductsContainer = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 0.5rem;
  overflow-x: hidden;

  @media (min-width: 768px) {
    padding: 0 1rem;
  }

  @media (min-width: 1024px) {
    padding: 0 2rem;
  }
`;

const Header = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1rem;
  margin-bottom: 1.5rem;

  @media (min-width: 768px) {
    flex-direction: row;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 2rem;
  }
`;

const Title = styled.h1`
  color: #333;
  font-size: 1.5rem;
  margin: 0;

  @media (min-width: 768px) {
    font-size: 2rem;
  }
`;

const SyncButton = styled.button`
  padding: 0.6rem 1rem;
  background-color: #2196F3;
  color: white;
  border: none;
  border-radius: 4px;
  cursor: pointer;
  font-size: 0.9rem;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
  width: 100%;

  &:hover {
    background-color: #1976D2;
  }

  &:disabled {
    background-color: #ccc;
    cursor: not-allowed;
  }

  .spinning {
    animation: spin 1s linear infinite;
  }

  @keyframes spin {
    from {
      transform: rotate(0deg);
    }
    to {
      transform: rotate(360deg);
    }
  }

  @media (min-width: 768px) {
    padding: 0.75rem 1.5rem;
    font-size: 1rem;
    width: auto;
  }
`;

const FilterSection = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.75rem;

  @media (min-width: 768px) {
    flex-direction: row;
    gap: 1rem;
    align-items: center;
  }
`;

const SearchBar = styled.input`
  padding: 0.75rem;
  border: 1px solid #ddd;
  border-radius: 4px;
  font-size: 1rem;
  width: 100%;

  @media (min-width: 768px) {
    width: 300px;
  }
`;

const CategoryFilter = styled.select`
  padding: 0.6rem;
  border: 1px solid #ddd;
  border-radius: 4px;
  font-size: 0.85rem;
  background-color: white;
  cursor: pointer;
  width: 100%;
  max-width: 150px;

  @media (min-width: 1024px) {
    padding: 0.75rem;
    font-size: 1rem;
    max-width: none;
    width: auto;
  }
`;

const TableWrapper = styled.div`
  display: none;
  overflow-x: auto;
  -webkit-overflow-scrolling: touch;
  background: white;
  border-radius: 8px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);

  @media (min-width: 768px) {
    display: block;
  }
`;

const ProductsTable = styled.table`
  width: 100%;
  border-collapse: collapse;
  min-width: 650px;
`;

const TableHeader = styled.th`
  background-color: #f8f9fa;
  padding: 0.6rem 0.5rem;
  text-align: left;
  font-weight: 600;
  font-size: 0.85rem;
  white-space: nowrap;

  @media (min-width: 1024px) {
    padding: 1rem;
    font-size: 1rem;
  }
  color: #555;
  border-bottom: 2px solid #dee2e6;
`;

const TableRow = styled.tr`
  &:hover {
    background-color: #f8f9fa;
  }
`;

const TableCell = styled.td`
  padding: 0.6rem 0.5rem;
  border-bottom: 1px solid #dee2e6;
  font-size: 0.85rem;

  @media (min-width: 1024px) {
    padding: 1rem;
    font-size: 1rem;
  }
`;

// Mobile Card Components
const MobileCardList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1rem;

  @media (min-width: 768px) {
    display: none;
  }
`;

const MobileCard = styled.div`
  background: white;
  border-radius: 8px;
  padding: 1rem;
  box-shadow: 0 2px 4px rgba(0,0,0,0.1);
  border-left: 4px solid ${props => {
    const cat = props.$category?.toLowerCase() || '';
    if (cat.includes('moldura')) return '#1976d2';
    if (cat.includes('cristal')) return '#388e3c';
    if (cat.includes('passepartout') || cat.includes('passepartous')) return '#c2185b';
    if (cat.includes('trasera')) return '#7b1fa2';
    if (cat.includes('accesorio')) return '#f57c00';
    return '#9e9e9e';
  }};
`;

const MobileCardHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  gap: 0.75rem;
  margin-bottom: 0.75rem;
`;

const MobileCardTitle = styled.div`
  font-weight: 600;
  color: #333;
  font-size: 0.95rem;
  flex: 1;
  margin-right: 0.5rem;
`;

const MobileCardCode = styled.div`
  font-size: 0.8rem;
  color: #666;
  margin-top: 0.25rem;
`;

const MobileCardBody = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 0.5rem;
`;

const MobileCardRow = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.15rem;
`;

const MobileCardLabel = styled.span`
  font-size: 0.75rem;
  color: #888;
`;

const MobileCardValue = styled.span`
  font-size: 0.9rem;
  color: #333;
  font-weight: 500;
`;

const CategoryBadge = styled.span`
  display: inline-block;
  padding: 0.25rem 0.75rem;
  border-radius: 12px;
  font-size: 0.875rem;
  background-color: ${props => {
    const cat = props.category?.toLowerCase() || '';
    if (cat.includes('moldura')) return '#e3f2fd';
    if (cat.includes('cristal')) return '#e8f5e9';
    if (cat.includes('passepartout') || cat.includes('passepartous')) return '#fce4ec';
    if (cat.includes('trasera')) return '#f3e5f5';
    if (cat.includes('accesorio')) return '#fff3e0';
    return '#e0e0e0';
  }};
  color: ${props => {
    const cat = props.category?.toLowerCase() || '';
    if (cat.includes('moldura')) return '#1976d2';
    if (cat.includes('cristal')) return '#388e3c';
    if (cat.includes('passepartout') || cat.includes('passepartous')) return '#c2185b';
    if (cat.includes('trasera')) return '#7b1fa2';
    if (cat.includes('accesorio')) return '#f57c00';
    return '#616161';
  }};
`;

const PriceInfo = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
`;

const MainPrice = styled.span`
  font-weight: 500;
  color: #333;
`;

const SecondaryPrice = styled.span`
  font-size: 0.875rem;
  color: #666;
`;

const StockIndicator = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  color: ${props => props.inStock ? '#4CAF50' : '#f44336'};
  font-weight: 500;
`;

const ProductImage = styled.img`
  width: 50px;
  height: 50px;
  object-fit: contain;
  border-radius: 4px;
  background: #f5f5f5;
`;

const ProductImagePlaceholder = styled.div`
  width: 50px;
  height: 50px;
  border-radius: 4px;
  background: #f0f0f0;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #ccc;
  font-size: 0.7rem;
`;

const MobileProductImage = styled.img`
  width: 40px;
  height: 40px;
  object-fit: contain;
  border-radius: 4px;
  background: #f5f5f5;
  flex-shrink: 0;
`;

const MobileProductImagePlaceholder = styled.div`
  width: 40px;
  height: 40px;
  border-radius: 4px;
  background: #f0f0f0;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #ccc;
  font-size: 0.6rem;
  flex-shrink: 0;
`;

const EmptyState = styled.div`
  text-align: center;
  padding: 3rem;
  color: #666;
`;

const LoadingState = styled.div`
  text-align: center;
  padding: 3rem;
  color: #666;
`;

const PaginationContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 0.75rem;
  margin: 1.5rem 0;
  padding: 1rem;

  @media (min-width: 768px) {
    flex-direction: row;
    justify-content: center;
    gap: 1rem;
    margin: 2rem 0;
  }
`;

const PaginationButtons = styled.div`
  display: flex;
  flex-wrap: wrap;
  justify-content: center;
  gap: 0.5rem;
`;

const PaginationButton = styled.button`
  padding: 0.5rem 0.75rem;
  border: 1px solid #ddd;
  background: ${props => props.active ? '#2196F3' : 'white'};
  color: ${props => props.active ? 'white' : '#333'};
  border-radius: 4px;
  cursor: pointer;
  transition: all 0.3s;
  font-size: 0.85rem;
  min-width: 40px;

  &:hover:not(:disabled) {
    background: ${props => props.active ? '#1976D2' : '#f5f5f5'};
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }

  @media (min-width: 768px) {
    padding: 0.5rem 1rem;
    font-size: 1rem;
  }
`;

const PageInfo = styled.span`
  color: #666;
  font-size: 0.85rem;

  @media (min-width: 768px) {
    font-size: 0.9rem;
  }
`;

const ProductsInfo = styled.div`
  text-align: center;
  padding: 0.5rem;
  color: #666;
  font-size: 0.85rem;
  border-bottom: 1px solid #e0e0e0;
  margin-bottom: 1rem;

  @media (min-width: 768px) {
    font-size: 0.9rem;
  }
`;

const Products = () => {
  const [searchTerm, setSearchTerm] = useState('');
  const [categoryFilter, setCategoryFilter] = useState('all');
  const [isSyncing, setIsSyncing] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(10); // Mostrar 10 productos por página
  const queryClient = useQueryClient();

  const { data: products, isLoading, error } = useQuery({
    queryKey: ['products'],
    queryFn: apiService.getAllProducts,
  });

  const categories = [
    { value: 'all', label: 'Todas las categorías' },
    { value: 'moldura', label: 'Molduras' },
    { value: 'passepartout', label: 'Passepartout' },
    { value: 'cristal', label: 'Cristales' },
    { value: 'trasera', label: 'Traseras' },
    { value: 'accesorio', label: 'Accesorios' }
  ];

  const handleSyncOdoo = async () => {
    setIsSyncing(true);
    try {
      const response = await api.post('/odoo-sync/sync-products', {
        updateExisting: true,
        onlyNew: false,
        limit: 1000
      });
      
      if (response.data.success) {
        const stats = response.data.stats;
        toast.success(`Sincronización completada: ${stats.created} creados, ${stats.updated} actualizados`);
        queryClient.invalidateQueries(['products']);
      } else {
        toast.error('Error durante la sincronización');
      }
    } catch (error) {
      console.error('Error syncing with Odoo:', error);
      toast.error(`Error de sincronización: ${error.response?.data?.details || error.message}`);
    } finally {
      setIsSyncing(false);
    }
  };

  const filteredProducts = products?.filter(product => {
    const matchesSearch = product.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         product.code?.toLowerCase().includes(searchTerm.toLowerCase());

    // Comparación case-insensitive y busca coincidencia parcial
    // Maneja variantes: passepartout/passepartous (con/sin 's')
    const matchesCategory = categoryFilter === 'all' || (() => {
      if (!product.categoryName) return false;
      const catName = product.categoryName.toLowerCase();
      const filterValue = categoryFilter.toLowerCase();

      // Para passepartout, buscar también passepartous
      if (filterValue === 'passepartout') {
        return catName.includes('passepartout') || catName.includes('passepartous');
      }

      return catName.includes(filterValue);
    })();

    return matchesSearch && matchesCategory;
  }) || [];

  // Calcular paginación
  const totalPages = Math.ceil(filteredProducts.length / itemsPerPage);
  const indexOfLastItem = currentPage * itemsPerPage;
  const indexOfFirstItem = indexOfLastItem - itemsPerPage;
  const currentProducts = filteredProducts.slice(indexOfFirstItem, indexOfLastItem);

  // Resetear a página 1 cuando cambia el filtro
  useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm, categoryFilter]);

  if (isLoading) {
    return (
      <ProductsContainer>
        <LoadingState>Cargando productos...</LoadingState>
      </ProductsContainer>
    );
  }

  if (error) {
    toast.error('Error al cargar los productos');
    return (
      <ProductsContainer>
        <EmptyState>Error al cargar los productos</EmptyState>
      </ProductsContainer>
    );
  }

  return (
    <ProductsContainer>
      <Header>
        <Title>Productos</Title>
        <FilterSection>
          <SyncButton onClick={handleSyncOdoo} disabled={isSyncing}>
            <FaSync className={isSyncing ? 'spinning' : ''} />
            {isSyncing ? 'Sincronizando...' : 'Sincronizar con Odoo'}
          </SyncButton>
          <SearchBar
            type="text"
            placeholder="Buscar por nombre o código..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
          <CategoryFilter
            value={categoryFilter}
            onChange={(e) => setCategoryFilter(e.target.value)}
          >
            {categories.map(cat => (
              <option key={cat.value} value={cat.value}>
                {cat.label}
              </option>
            ))}
          </CategoryFilter>
        </FilterSection>
      </Header>

      {filteredProducts.length === 0 ? (
        <EmptyState>
          {searchTerm || categoryFilter !== 'all' 
            ? 'No se encontraron productos' 
            : 'No hay productos registrados'}
        </EmptyState>
      ) : (
        <>
          <ProductsInfo>
            Mostrando {indexOfFirstItem + 1}-{Math.min(indexOfLastItem, filteredProducts.length)} de {filteredProducts.length} productos
          </ProductsInfo>

          {/* Mobile Card View */}
          <MobileCardList>
            {currentProducts.map(product => {
              const categoryName = product.categoryName?.toLowerCase() || '';
              const matchedCategory = categories.find(c => {
                if (c.value === 'all') return false;
                if (c.value === 'passepartout') {
                  return categoryName.includes('passepartout') || categoryName.includes('passepartous');
                }
                return categoryName.includes(c.value.toLowerCase());
              });

              return (
                <MobileCard key={product.id} $category={product.categoryName}>
                  <MobileCardHeader>
                    {product.image ? (
                      <MobileProductImage src={product.image} alt={product.name} />
                    ) : (
                      <MobileProductImagePlaceholder>Sin img</MobileProductImagePlaceholder>
                    )}
                    <MobileCardTitle>
                      {product.name}
                      <MobileCardCode>{product.code || '-'}</MobileCardCode>
                    </MobileCardTitle>
                    <StockIndicator inStock={product.isActive}>
                      {product.isActive ? '●' : '○'}
                    </StockIndicator>
                  </MobileCardHeader>
                  <MobileCardBody>
                    <MobileCardRow>
                      <MobileCardLabel>Categoría</MobileCardLabel>
                      <MobileCardValue>
                        {matchedCategory?.label || product.categoryName || '-'}
                      </MobileCardValue>
                    </MobileCardRow>
                    <MobileCardRow>
                      <MobileCardLabel>Precio</MobileCardLabel>
                      <MobileCardValue>
                        {formatCurrency(product.basePrice || product.list_price || 0)}
                        {product.unit === 'm2' && '/m²'}
                      </MobileCardValue>
                    </MobileCardRow>
                    <MobileCardRow>
                      <MobileCardLabel>Unidad</MobileCardLabel>
                      <MobileCardValue>
                        {product.unit || (categoryName.includes('moldura') ? 'ml' : 'm2')}
                      </MobileCardValue>
                    </MobileCardRow>
                    <MobileCardRow>
                      <MobileCardLabel>Estado</MobileCardLabel>
                      <MobileCardValue>
                        {product.isActive ? 'Disponible' : 'No disponible'}
                      </MobileCardValue>
                    </MobileCardRow>
                  </MobileCardBody>
                </MobileCard>
              );
            })}
          </MobileCardList>

          {/* Desktop Table View */}
          <TableWrapper>
          <ProductsTable>
          <thead>
            <tr>
              <TableHeader style={{ width: '60px' }}>Img</TableHeader>
              <TableHeader>Código</TableHeader>
              <TableHeader>Nombre</TableHeader>
              <TableHeader>Categoría</TableHeader>
              <TableHeader>Precio</TableHeader>
              <TableHeader>Unidad</TableHeader>
              <TableHeader>Stock</TableHeader>
            </tr>
          </thead>
          <tbody>
            {currentProducts.map(product => (
              <TableRow key={product.id}>
                <TableCell>
                  {product.image ? (
                    <ProductImage src={product.image} alt={product.name} />
                  ) : (
                    <ProductImagePlaceholder>Sin img</ProductImagePlaceholder>
                  )}
                </TableCell>
                <TableCell>{product.code || '-'}</TableCell>
                <TableCell>
                  <strong>{product.name}</strong>
                  {product.description && (
                    <div style={{ fontSize: '0.875rem', color: '#666', marginTop: '0.25rem' }}>
                      {product.description}
                    </div>
                  )}
                </TableCell>
                <TableCell>
                  <CategoryBadge category={product.categoryName}>
                    {(() => {
                      const categoryName = product.categoryName?.toLowerCase() || '';

                      // Buscar coincidencia, manejando variantes como passepartous
                      const matchedCategory = categories.find(c => {
                        if (c.value === 'all') return false;

                        // Para passepartout, buscar también passepartous
                        if (c.value === 'passepartout') {
                          return categoryName.includes('passepartout') || categoryName.includes('passepartous');
                        }

                        return categoryName.includes(c.value.toLowerCase());
                      });

                      return matchedCategory?.label || product.categoryName;
                    })()}
                  </CategoryBadge>
                </TableCell>
                <TableCell>
                  <PriceInfo>
                    <MainPrice>{formatCurrency(product.basePrice || product.list_price || 0)}</MainPrice>
                    {product.unit === 'm2' && (
                      <SecondaryPrice>
                        por m²
                      </SecondaryPrice>
                    )}
                  </PriceInfo>
                </TableCell>
                <TableCell>
                  {product.unit ||
                   (product.categoryName?.toLowerCase().includes('moldura') ? 'ml' : 'm2')}
                </TableCell>
                <TableCell>
                  <StockIndicator inStock={product.isActive}>
                    {product.isActive ? '● Disponible' : '○ No disponible'}
                  </StockIndicator>
                </TableCell>
              </TableRow>
            ))}
          </tbody>
        </ProductsTable>
          </TableWrapper>

          {totalPages > 1 && (
            <PaginationContainer>
              <PaginationButtons>
                <PaginationButton
                  onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
                  disabled={currentPage === 1}
                >
                  ←
                </PaginationButton>

                {/* Mostrar números de página */}
                {[...Array(Math.min(5, totalPages))].map((_, i) => {
                  let pageNum;
                  if (totalPages <= 5) {
                    pageNum = i + 1;
                  } else if (currentPage <= 3) {
                    pageNum = i + 1;
                  } else if (currentPage >= totalPages - 2) {
                    pageNum = totalPages - 4 + i;
                  } else {
                    pageNum = currentPage - 2 + i;
                  }

                  return (
                    <PaginationButton
                      key={pageNum}
                      active={pageNum === currentPage}
                      onClick={() => setCurrentPage(pageNum)}
                    >
                      {pageNum}
                    </PaginationButton>
                  );
                })}

                <PaginationButton
                  onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))}
                  disabled={currentPage === totalPages}
                >
                  →
                </PaginationButton>
              </PaginationButtons>

              <PageInfo>
                Página {currentPage} de {totalPages}
              </PageInfo>
            </PaginationContainer>
          )}
        </>
      )}
    </ProductsContainer>
  );
};

export default Products;